/** Default sort used by the courses list screen and its cache keys. */
export const COURSE_LIST_DEFAULTS = {
  size: 10,
  sortBy: 'courseName',
  sortDir: 'asc',
};

/** API sortBy values exposed by the courses table column headers. */
export const COURSE_SORT_FIELDS = {
  courseName: 'courseName',
  code: 'code',
  start: 'start',
  expire: 'expire',
  active: 'active',
};

/**
 * Computes the next client-side sort when a column header is clicked.
 * Same column toggles asc/desc; a new column always starts asc.
 */
export function getNextCourseSort(columnKey, currentSortBy, currentSortDir) {
  const sortBy = columnKey || COURSE_LIST_DEFAULTS.sortBy;
  if (currentSortBy === sortBy) {
    return {
      sortBy,
      sortDir: currentSortDir === 'asc' ? 'desc' : 'asc',
    };
  }
  return { sortBy, sortDir: 'asc' };
}

/**
 * Stable cache key for a paginated course list query.
 * Keyed by userId + page + size + keyword (sort is client-side only).
 */
export function buildCourseListCacheKey(userId, {
  page = 0,
  size = COURSE_LIST_DEFAULTS.size,
  keyword = '',
} = {}) {
  const kw = (keyword || '').trim();
  return `courses:${userId}:${page}:${size}:${kw}`;
}

/**
 * Builds query string for paginated course catalog API calls.
 * Sorting is applied client-side; the API is not called on column-header clicks.
 */
export function buildCourseListQuery({
  page = 0,
  size = 10,
  keyword = '',
} = {}) {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('size', String(size));
  if (keyword && keyword.trim()) {
    params.set('keyword', keyword.trim());
  }
  return params.toString();
}

/** Resolves a row field value for client-side column sorting. */
function courseSortValue(row, sortBy) {
  switch (sortBy) {
    case COURSE_SORT_FIELDS.code:
      return row.code ?? '';
    case COURSE_SORT_FIELDS.start:
      return row.start ? new Date(row.start).getTime() : 0;
    case COURSE_SORT_FIELDS.expire:
      return row.expire ? new Date(row.expire).getTime() : 0;
    case COURSE_SORT_FIELDS.active:
      return row.active ?? 0;
    case COURSE_SORT_FIELDS.courseName:
    default:
      return row.courseName ?? '';
  }
}

/**
 * Sorts the current page of course rows in memory — no API round-trip.
 */
export function sortCoursesLocally(rows, sortBy, sortDir) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const dir = sortDir === 'desc' ? -1 : 1;
  const field = sortBy || COURSE_LIST_DEFAULTS.sortBy;

  list.sort((a, b) => {
    const av = courseSortValue(a, field);
    const bv = courseSortValue(b, field);
    if (typeof av === 'number' && typeof bv === 'number') {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir;
  });

  return list;
}

/**
 * Parses paginated course list API response; falls back to array payloads for legacy callers.
 */
export function parseCourseListResponse(payload) {
  if (Array.isArray(payload)) {
    return {
      content: payload,
      totalElements: payload.length,
      totalPages: 1,
      page: 0,
      size: payload.length,
    };
  }

  return {
    content: Array.isArray(payload?.content) ? payload.content : [],
    totalElements: Number(payload?.totalElements ?? 0),
    totalPages: Number(payload?.totalPages ?? 0),
    page: Number(payload?.page ?? 0),
    size: Number(payload?.size ?? 10),
  };
}
